import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { resetPassword } from '../services/api';
import { AUTH_CSS, BrandLogo } from './AuthPage';

function getStrength(pw) {
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[a-z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  return score;
}

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const strength = useMemo(() => getStrength(password), [password]);

  const requirements = [
    { label: 'At least 8 characters', met: password.length >= 8 },
    { label: 'Uppercase letter', met: /[A-Z]/.test(password) },
    { label: 'Lowercase letter', met: /[a-z]/.test(password) },
    { label: 'Number', met: /[0-9]/.test(password) },
    { label: 'Special character', met: /[^A-Za-z0-9]/.test(password) },
  ];

  const allMet = requirements.every(r => r.met);
  const passwordsMatch = password === confirm && confirm.length > 0;
  const isValid = allMet && passwordsMatch && token;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid || loading) return;
    setError('');
    setLoading(true);
    try {
      await resetPassword(token, password);
      setPassword('');
      setConfirm('');
      setSuccess(true);
    } catch (err) {
      const msg = err.message || 'Something went wrong';
      if (msg.toLowerCase().includes('expired') || msg.toLowerCase().includes('invalid')) {
        setError('This reset link has expired or is invalid. Please request a new one.');
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="auth-root">
        <style>{AUTH_CSS}</style>
        <Link to="/login" className="auth-back-link">← Back to login</Link>
        <div className="auth-center">
          <div className="auth-identity">
            <BrandLogo />
            <span className="auth-identity__name">DS CodeXAI</span>
            <span className="auth-identity__tag">Autonomous Software Engineering Platform</span>
          </div>
          <div className="auth-card">
            <div className="auth-success">
              <div className="auth-success__icon">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                  <circle cx="24" cy="24" r="24" fill="rgba(239, 68, 68, 0.10)" />
                  <path d="M17 17l14 14M31 17L17 31" stroke="#ef4444" strokeWidth="3" strokeLinecap="round" />
                </svg>
              </div>
              <h2 className="auth-card__title" style={{ textAlign: 'center' }}>Invalid Link</h2>
              <p className="auth-success__msg">
                This password reset link is missing or invalid. Please request a new one.
              </p>
              <Link to="/forgot-password" className="auth-btn auth-success__btn">
                Request New Link
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-root">
      <style>{AUTH_CSS}</style>
      <Link to="/login" className="auth-back-link">← Back to login</Link>

      <div className="auth-center">
        <div className="auth-identity">
          <BrandLogo />
          <span className="auth-identity__name">DS CodeXAI</span>
          <span className="auth-identity__tag">Autonomous Software Engineering Platform</span>
        </div>

        <div className="auth-card">
          {!success ? (
            <>
              <div className="auth-card__header">
                <h2 className="auth-card__title">Set new password</h2>
                <p className="auth-card__sub">
                  Create a strong password for your account.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="auth-form">
                <div className="auth-field">
                  <label className="auth-label">New Password</label>
                  <div className="auth-input-wrap">
                    <input
                      ref={inputRef}
                      type={showPw ? 'text' : 'password'}
                      value={password}
                      onChange={e => { setPassword(e.target.value); setError(''); }}
                      placeholder="••••••••"
                      required
                      className="auth-input"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="auth-eye-btn"
                      onClick={() => setShowPw(!showPw)}
                      tabIndex={-1}
                      aria-label={showPw ? 'Hide password' : 'Show password'}
                    >
                      {showPw ? '◡' : '◠'}
                    </button>
                  </div>

                  {/* Strength indicator */}
                  {password && (
                    <div className="auth-strength">
                      {[1, 2, 3, 4, 5].map(i => (
                        <div
                          key={i}
                          className={`auth-strength__bar${strength >= i ? ` auth-strength__bar--filled-${i}` : ''}`}
                        />
                      ))}
                    </div>
                  )}

                  {/* Requirements */}
                  {password && (
                    <ul className="auth-requirements">
                      {requirements.map((r, i) => (
                        <li key={i} className={r.met ? 'met' : ''}>
                          <span className="auth-req-icon">{r.met ? '✓' : ''}</span>
                          {r.label}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="auth-field">
                  <label className="auth-label">Confirm Password</label>
                  <div className="auth-input-wrap">
                    <input
                      type={showConfirm ? 'text' : 'password'}
                      value={confirm}
                      onChange={e => { setConfirm(e.target.value); setError(''); }}
                      placeholder="••••••••"
                      required
                      className="auth-input"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="auth-eye-btn"
                      onClick={() => setShowConfirm(!showConfirm)}
                      tabIndex={-1}
                      aria-label={showConfirm ? 'Hide password' : 'Show password'}
                    >
                      {showConfirm ? '◡' : '◠'}
                    </button>
                  </div>
                  {confirm && !passwordsMatch && (
                    <span className="auth-field-error">Passwords do not match</span>
                  )}
                </div>

                {error && (
                  <div className="auth-error">
                    <span className="auth-error__icon">⚠</span>
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={!isValid || loading}
                  className={`auth-btn${loading ? ' auth-btn--loading' : ''}`}
                >
                  {loading ? 'Resetting…' : 'Reset Password'}
                </button>
              </form>
            </>
          ) : (
            <div className="auth-success">
              <div className="auth-success__icon">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                  <circle cx="24" cy="24" r="24" fill="rgba(34, 197, 94, 0.12)" />
                  <path d="M15 25l6 6 12-12" stroke="#22c55e" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h2 className="auth-card__title" style={{ textAlign: 'center' }}>Password updated</h2>
              <p className="auth-success__msg">
                Your password has been updated successfully.
              </p>
              <Link to="/login" className="auth-btn auth-success__btn">
                Continue to Login
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
